import React from "react"
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

export type SortDirection = 'asc' | 'desc';

const DEFAULT_HEADER_CELL =
  'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';

const SORT_ACTIVE_COLOR = '#51a0b5'
const SORT_INACTIVE_CHEVRON = '#d1d5db'

type SortDirectionIconsProps = {
  direction: SortDirection | 'idle'
}

const SortDirectionIcons: React.FC<SortDirectionIconsProps> = ({ direction }) => (
  <span
    className="inline-flex flex-col items-center justify-center w-[18px] h-[18px] -space-y-[10px]"
    aria-hidden="true"
  >
    <ArrowDropUpIcon
      sx={{
        fontSize: 18,
        color: direction === 'asc' ? SORT_ACTIVE_COLOR : SORT_INACTIVE_CHEVRON,
        display: 'block',
      }}
    />
    <ArrowDropDownIcon
      sx={{
        fontSize: 18,
        color: direction === 'desc' ? SORT_ACTIVE_COLOR : SORT_INACTIVE_CHEVRON,
        display: 'block',
      }}
    />
  </span>
)

export type SortableHeaderProps<T extends string> = {
  label: string
  field: T
  sortField: T
  sortDirection: SortDirection
  onSort: (field: T) => void
  cellClassName?: string
  activeCellClassName?: string
}

const SortableHeader = <T extends string>({
  label,
  field,
  sortField,
  sortDirection,
  onSort,
  cellClassName = DEFAULT_HEADER_CELL,
  activeCellClassName = 'text-primary-300',
}: SortableHeaderProps<T>) => {
  const isActive = sortField === field

  return (
    <th
      scope="col"
      className={[cellClassName, isActive ? activeCellClassName : ''].filter(Boolean).join(' ')}
      aria-sort={isActive ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(field)}
        className="inline-flex items-center gap-1 uppercase text-inherit font-inherit transition-colors"
        aria-label={`Sort by ${label}${isActive ? `, currently ${sortDirection === 'asc' ? 'ascending' : 'descending'}` : ''}`}
      >
        <span>{label}</span>
        <span className="inline-flex shrink-0">
          {isActive ? (
            sortDirection === 'asc'
              ? <SortDirectionIcons direction="asc" />
              : <SortDirectionIcons direction="desc" />
          ) : (
            <SortDirectionIcons direction="idle" />
          )}
        </span>
      </button>
    </th>
  )
}

export default SortableHeader
